import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hrService, payrollService, attendanceService, comprehensiveHRService } from '../../lib/checkInAttendanceService';
import Icon from '../../components/AppIcon';
import EmployeeDirectory from './components/EmployeeDirectory';
import PayrollProcessing from './components/PayrollProcessing';
import LeaveApproval from './components/LeaveApproval';
import HRAnalytics from './components/HRAnalytics';
import ShiftScheduling from './components/ShiftScheduling';
import TrainerMetrics from './components/TrainerMetrics';
import ComplianceDocuments from './components/ComplianceDocuments';
import PerformanceReviews from './components/PerformanceReviews';
import HRNotifications from './components/HRNotifications';

const PayrollHRManagementSystem = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('employees');
  const [hrProfiles, setHrProfiles] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const isManager = ['manager', 'owner', 'sysadmin']?.includes(userProfile?.role);

  useEffect(() => {
    if (user?.id && isManager) {
      loadData();
    }
  }, [user, isManager]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadHRProfiles(),
      loadPayrollRecords(),
      loadLeaveRequests(),
      loadAttendanceData(),
      loadShifts(),
      loadNotifications()
    ]);
    setLoading(false);
  };

  const loadHRProfiles = async () => {
    if (!userProfile?.gym_id) return;
    const { data } = await hrService?.getGymHRProfiles(userProfile?.gym_id);
    if (data) setHrProfiles(data);
  };

  const loadPayrollRecords = async () => {
    if (!userProfile?.gym_id) return;
    const { data } = await payrollService?.getGymPayroll(userProfile?.gym_id);
    if (data) setPayrollRecords(data);
  };

  const loadLeaveRequests = async () => {
    if (!userProfile?.gym_id) return;
    const { data } = await hrService?.getGymLeaveRequests(userProfile?.gym_id);
    if (data) setLeaveRequests(data);
  };

  const loadAttendanceData = async () => {
    if (!userProfile?.gym_id) return;
    const { data } = await attendanceService?.getGymAttendance(userProfile?.gym_id);
    if (data) setAttendanceData(data);
  };

  const loadShifts = async () => {
    if (!userProfile?.gym_id) return;
    const { data } = await comprehensiveHRService?.getGymShifts(userProfile?.gym_id);
    if (data) setShifts(data);
  };

  const loadNotifications = async () => {
    if (!user?.id) return;
    const { data } = await comprehensiveHRService?.getStaffNotifications(user?.id);
    if (data) setNotifications(data);
  };

  const tabs = [
    { id: 'employees', label: 'Employee Directory', icon: 'Users' },
    { id: 'scheduling', label: 'Shift Scheduling', icon: 'Calendar' },
    { id: 'payroll', label: 'Payroll Processing', icon: 'DollarSign' },
    { id: 'leave', label: 'Leave Management', icon: 'Palmtree' },
    { id: 'trainer-metrics', label: 'Trainer Metrics', icon: 'TrendingUp' },
    { id: 'performance', label: 'Performance Reviews', icon: 'Award' },
    { id: 'compliance', label: 'Compliance & Docs', icon: 'FileText' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'analytics', label: 'HR Analytics', icon: 'BarChart3' }
  ];

  if (!isManager) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="card max-w-md text-center">
          <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">
            This page is only accessible to managers, owners, and system administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Comprehensive HR Management System</h1>
          <p className="text-muted-foreground">
            Complete employee lifecycle management, scheduling, payroll, performance tracking, and compliance
          </p>
        </div>

        {/* Tabs */}
        <div className="card mb-6">
          <div className="flex border-b border-border overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center justify-center gap-2 py-4 px-6 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={20} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'employees' && (
            <EmployeeDirectory
              hrProfiles={hrProfiles}
              loading={loading}
              onRefresh={loadHRProfiles}
              gymId={userProfile?.gym_id}
            />
          )}

          {activeTab === 'scheduling' && (
            <ShiftScheduling
              shifts={shifts}
              hrProfiles={hrProfiles}
              loading={loading}
              onRefresh={loadShifts}
              gymId={userProfile?.gym_id}
              managerId={user?.id}
            />
          )}

          {activeTab === 'payroll' && (
            <PayrollProcessing
              payrollRecords={payrollRecords}
              hrProfiles={hrProfiles}
              attendanceData={attendanceData}
              loading={loading}
              onRefresh={loadPayrollRecords}
              gymId={userProfile?.gym_id}
              managerId={user?.id}
            />
          )}

          {activeTab === 'leave' && (
            <LeaveApproval
              leaveRequests={leaveRequests}
              loading={loading}
              onRefresh={loadLeaveRequests}
              managerId={user?.id}
            />
          )}

          {activeTab === 'trainer-metrics' && (
            <TrainerMetrics
              hrProfiles={hrProfiles}
              loading={loading}
              gymId={userProfile?.gym_id}
            />
          )}

          {activeTab === 'performance' && (
            <PerformanceReviews
              hrProfiles={hrProfiles}
              loading={loading}
              gymId={userProfile?.gym_id}
              managerId={user?.id}
            />
          )}

          {activeTab === 'compliance' && (
            <ComplianceDocuments
              hrProfiles={hrProfiles}
              loading={loading}
              gymId={userProfile?.gym_id}
              managerId={user?.id}
            />
          )}

          {activeTab === 'notifications' && (
            <HRNotifications
              notifications={notifications}
              loading={loading}
              onRefresh={loadNotifications}
              staffId={user?.id}
            />
          )}

          {activeTab === 'analytics' && (
            <HRAnalytics
              hrProfiles={hrProfiles}
              payrollRecords={payrollRecords}
              attendanceData={attendanceData}
              leaveRequests={leaveRequests}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PayrollHRManagementSystem;